import type { CodingScreenConfig } from './schema';

export const DEFAULT_CODING_SCREEN_CONFIG = {
  mode: 'coding',
  header: {
    topBar: { contentKey: 'topBar', visible: true, items: [] },
    patientSummary: {
      contentKey: 'patientSummary',
      visible: true,
      fields: [
        {
          contentKey: 'patientInfo',
          order: 0,
          visible: true,
          label: 'Patient',
          contentId: 'case.patient',
          componentId: 'PatientInfo'
        },
        {
          contentKey: 'caseSpecialty',
          order: 1,
          visible: true,
          label: 'Specialty',
          contentId: 'case.specialty',
          componentId: 'CaseSpecialty'
        },
        {
          contentKey: 'careTeam',
          order: 2,
          visible: true,
          label: 'Care Team',
          contentId: 'case.providers',
          componentId: 'CareTeam'
        },
        {
          contentKey: 'billingLocation',
          order: 3,
          visible: true,
          label: 'Facility',
          contentId: 'case.billingLocation',
          componentId: 'BillingLocation'
        },
        {
          contentKey: 'encounter',
          order: 4,
          visible: true,
          label: 'Encounter',
          contentId: 'case.encounter',
          componentId: 'Encounter'
        },
        {
          contentKey: 'holdAction',
          order: 5,
          visible: true,
          label: 'Hold',
          contentId: 'session.currentUser',
          componentId: 'HoldAction',
          presentation: 'plain'
        },
        {
          // Must stay ahead of Notes/Activity: PatientSummaryBar slices its action fields from
          // the FIRST Notes/Activity key to the end, so a later order lands inside that pair's
          // tight-gapped group, on the wrong side of its divider.
          contentKey: 'transferAction',
          order: 6,
          visible: true,
          label: 'Transfer',
          contentId: 'case.transfer',
          componentId: 'TransferAction',
          presentation: 'plain'
        },
        {
          // Same ordering constraint as Transfer above. Not enforced by `validateConfig`, which
          // checks only that `order` values are unique and finite.
          contentKey: 'excludeAction',
          order: 7,
          visible: true,
          label: 'Exclude',
          // `case.*` keeps a grading config from reusing THIS binding (the namespace gate rejects it).
          // It does not make the component coder-only — `ExcludeAction` reads no `mode`.
          contentId: 'case.excludeContext',
          componentId: 'ExcludeAction',
          // Without this the icon renders inside the info `Tag` chip instead of bare.
          presentation: 'plain'
        },
        {
          contentKey: 'caseNotesAction',
          order: 8,
          visible: true,
          label: 'Notes',
          contentId: 'case.notesCount',
          componentId: 'CaseNotesAction',
          presentation: 'plain'
        },
        {
          contentKey: 'activityAction',
          order: 9,
          visible: true,
          label: 'Activity',
          componentId: 'ActivityAction',
          presentation: 'plain'
        }
      ]
    },
    caseContext: {
      contentKey: 'caseContext',
      visible: true,
      // `presentation` must NOT be set here — `validateConfig` rejects it on caseContext fields,
      // since only `PatientSummaryBar` honors it.
      fields: [
        {
          contentKey: 'patientTypeInfo',
          order: 0,
          visible: true,
          // 'Type', NOT 'Class' — the ticket corrects the mockup's `CLASS` label in prose, and the
          // value below is the case's own `patient_type`. The Legal status ROW inside the popover is
          // what carries `patient_class`, so labelling the pill 'Class' would name the wrong field.
          label: 'Type',
          contentId: 'case.patientType',
          componentId: 'PatientTypeInfo'
        },
        {
          // Renumbered from 0 — the bar paints strictly ascending by `order`, and
          // `CaseContextBar` paints strictly ascending by `order`, so Type taking 0 is what pushes
          // Payer to 1. Nothing binds to these numbers outside this list; `validateConfig` only
          // requires them to be unique and finite.
          contentKey: 'payerInfo',
          order: 1,
          visible: true,
          label: 'Payer',
          contentId: 'case.payer',
          componentId: 'PayerInfo'
        },
        {
          contentKey: 'flagsInfo',
          order: 2,
          visible: true,
          label: 'Flags',
          contentId: 'case.flags',
          componentId: 'FlagsInfo',
          valueChrome: 'compact'
        }
      ],
      routingContext: {
        contentKey: 'routingContext',
        contentId: 'case.routingContext',
        componentId: 'RoutingContext'
      }
    }
  },
  panes: [
    { id: 'left', order: 0, sizePct: 62, minPx: 480 },
    { id: 'right', order: 1, sizePct: 38, minPx: 360 }
  ],
  left: {
    // CODING ONLY: docNav/viewer hand the strip + body over to the injected
    // NoteNav/NoteViewer slot components, both bound to the same 'doc.notes' NotesData -- see
    // DEFAULT_GRADING_SCREEN_CONFIG below, which overrides this pair back to the pre-story
    // identity-only/doc.body shape (grading is out of scope for the note-display story).
    docNav: {
      contentKey: 'docNav',
      componentId: 'NoteNav',
      contentId: 'doc.notes'
    },
    viewer: {
      contentKey: 'viewer',
      contentId: 'doc.notes',
      componentId: 'NoteViewer'
    },
    tabs: []
  },
  right: {
    reviewTabs: { contentKey: 'reviewTabs' },
    reviewContent: { contentKey: 'reviewContent' },
    footerSummary: {
      contentKey: 'footerSummary',
      contentId: 'summary.reviewProgress'
    },
    tabs: [
      { contentKey: 'codes', order: 0, visible: true, label: 'Codes' },
      {
        contentKey: 'transactions',
        order: 1,
        visible: true,
        label: 'Transactions'
      }
    ],
    sections: [],
    actions: []
  },
  footer: {
    shortcutStatus: { contentKey: 'shortcutStatus' },
    caseIdentifier: {
      contentKey: 'caseIdentifier',
      contentId: 'case.identifier'
    },
    shortcuts: []
  }
} as const satisfies CodingScreenConfig;

export const DEFAULT_GRADING_SCREEN_CONFIG = {
  ...DEFAULT_CODING_SCREEN_CONFIG,
  mode: 'grading',
  header: {
    ...DEFAULT_CODING_SCREEN_CONFIG.header,
    patientSummary: {
      ...DEFAULT_CODING_SCREEN_CONFIG.header.patientSummary,
      fields: [
        {
          contentKey: 'patientInfo',
          order: 0,
          visible: true,
          label: 'Patient',
          contentId: 'workitem.patient',
          componentId: 'PatientInfo'
        },
        {
          contentKey: 'caseSpecialty',
          order: 1,
          visible: true,
          label: 'Specialty',
          contentId: 'workitem.specialty',
          componentId: 'CaseSpecialty'
        },
        {
          contentKey: 'billingLocation',
          order: 2,
          visible: true,
          label: 'Facility',
          contentId: 'workitem.billingLocation',
          componentId: 'BillingLocation'
        },
        {
          contentKey: 'activityAction',
          order: 3,
          visible: true,
          label: 'Activity',
          componentId: 'ActivityAction',
          presentation: 'plain'
        }
      ]
    },
    // Grading MUST override `caseContext`: this object spreads the coding header, and inheriting a
    // `case.`-namespaced field fails `validateConfig` in grading mode. Nothing to bind here anyway —
    // `CQAWorkItem` carries a bare `insurance` string with no payer id or plan. Listed explicitly, not
    // spread, so the coding-only `case.routingContext` slot is not inherited either.
    caseContext: {
      contentKey: DEFAULT_CODING_SCREEN_CONFIG.header.caseContext.contentKey,
      visible: DEFAULT_CODING_SCREEN_CONFIG.header.caseContext.visible,
      fields: []
    }
  },
  // The coding config's docNav/viewer are bound to the NoteNav/NoteViewer slots --
  // OUT OF SCOPE for grading (note display is coding-only), so grading overrides this pair back to
  // the pre-story identity-only marker / doc.body binding, unchanged from before that story.
  left: {
    ...DEFAULT_CODING_SCREEN_CONFIG.left,
    docNav: { contentKey: 'docNav' },
    viewer: { contentKey: 'viewer', contentId: 'doc.body' }
  },
  right: {
    ...DEFAULT_CODING_SCREEN_CONFIG.right,
    tabs: [
      { contentKey: 'spans', order: 0, visible: true, label: 'Spans' },
      { contentKey: 'codes', order: 1, visible: true, label: 'Codes' },
      {
        contentKey: 'transactions',
        order: 2,
        visible: true,
        label: 'Transactions'
      }
    ]
  },
  footer: {
    ...DEFAULT_CODING_SCREEN_CONFIG.footer,
    caseIdentifier: {
      contentKey: 'caseIdentifier',
      contentId: 'workitem.identifier'
    }
  }
} as const satisfies CodingScreenConfig;
